import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import Sidebar from "./Sidebar";
import ResumePreview from "./ResumePreview";
import { apiUrl } from "../config/apiBase";
import html2pdf from 'html2pdf.js';
import "./Dashboard.css";
import "./History.css";

const History = () => {
  const navigate = useNavigate();
  const { user, refreshTokenIfNeeded } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [hrQuestionSets, setHrQuestionSets] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get the current session to retrieve the access token
        const token = await refreshTokenIfNeeded();
        
        if (!token) {
          setError('Authentication required. Please sign in again.');
          setLoading(false);
          return;
        }

        // Fetch technical interview jobs
        const jobsResponse = await axios.get(apiUrl('/api/jobs'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setJobs(jobsResponse.data.jobs || []);

        // Fetch HR question sets
        const hrResponse = await axios.get(apiUrl('/api/hr-screening/history'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setHrQuestionSets(hrResponse.data.hr_question_sets || []);

        // Fetch resumes
        const resumesResponse = await axios.get(apiUrl('/api/resume-builder/resumes'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setResumes(resumesResponse.data.resumes || []);

      } catch (err) {
        console.error('Error fetching history:', err);
        setError('Failed to load history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user, refreshTokenIfNeeded]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getJobTitle = (job) => {
    // Try to get title from images first, then fallback to URL
    if (job.images && job.images.length > 0 && job.images[0].job_title) {
      const image = job.images[0];
      return `${image.job_title}${image.company ? ` at ${image.company}` : ''}`;
    }
    // Extract title from URL if no image data
    return job.job_url ? `Job from ${new URL(job.job_url).hostname}` : 'Untitled Job';
  };

  const getHrQuestionSetTitle = (hrSet) => {
    return `${hrSet.title}${hrSet.company ? ` at ${hrSet.company}` : ''}`;
  };

  const getResumeTitle = (resume) => {
    // Try to get name from resume data, fallback to generic title
    if (resume.personal_info && resume.personal_info.fullName) {
      return `${resume.personal_info.fullName}'s Resume`;
    }
    return `Resume - ${formatDate(resume.created_at)}`;
  };

  const getHrQuestionSetStatus = (hrSet) => {
    if (hrSet.completed_at) {
      return hrSet.interview_performance_scoring ? 'completed_with_scoring' : 'completed';
    }
    return 'in_progress';
  };

  // PDF download functionality for history resumes
  const handleDownloadResumePDF = async (resume) => {
    // If resume has a stored PDF URL, download it directly
    if (resume.pdf_url) {
      try {
        // Downloading stored PDF
        
        // Create a temporary link to download the PDF
        const link = document.createElement('a');
        link.href = resume.pdf_url;
        link.download = `${resume.personal_info?.fullName || 'Resume'}_Resume.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        return;
      } catch (error) {
        console.error('Error downloading stored PDF:', error);
        // Fall back to regeneration if direct download fails
      }
    }

    // Fallback: Regenerate PDF using the same ResumePreview component
    // No stored PDF found, regenerating from data
    
    // Construct resume_data from individual fields returned by backend
    const resumeData = {
      personalInfo: resume.personal_info || {},
      professionalSummary: resume.professional_summary || '',
      experience: resume.experience || [],
      education: resume.education || [],
      skills: resume.skills || ''
    };

    if (!resumeData || (!resumeData.personalInfo && !resumeData.professionalSummary && 
        (!resumeData.experience || resumeData.experience.length === 0) && 
        (!resumeData.education || resumeData.education.length === 0) && !resumeData.skills)) {
      alert('Resume data not available for download');
      return;
    }

    // Create a clean, isolated container for PDF generation
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.left = '0';
    tempContainer.style.top = '0';
    tempContainer.style.width = '794px'; // A4 width at 96 DPI
    tempContainer.style.zIndex = '-9999';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.overflow = 'visible';
    document.body.appendChild(tempContainer);

    // Use ReactDOM to render the ResumePreview component
    const { createRoot } = await import('react-dom/client');
    const root = createRoot(tempContainer);
    
    // Render the resume preview directly without extra wrapper
    await new Promise((resolve) => {
      root.render(
        <ResumePreview 
          personalInfo={resumeData.personalInfo}
          professionalSummary={resumeData.professionalSummary}
          experience={resumeData.experience}
          education={resumeData.education}
          skills={resumeData.skills}
          languageSkills={resumeData.languageSkills}
          showDownloadButton={false}
        />
      );
      // Give React time to fully render and apply styles
      setTimeout(resolve, 500);
    });

    // Get the actual resume content (traditional-template)
    const resumeElement = tempContainer.querySelector('.traditional-template');

    if (!resumeElement) {
      alert('Failed to render resume for download');
      document.body.removeChild(tempContainer);
      return;
    }

    // Force white background and proper sizing for reproducibility
    resumeElement.style.backgroundColor = '#ffffff';
    resumeElement.style.width = '794px';
    resumeElement.style.minHeight = 'auto';
    resumeElement.style.margin = '0';
    resumeElement.style.padding = '15mm';
    resumeElement.style.boxSizing = 'border-box';
    resumeElement.style.overflow = 'visible';

    const opt = {
      margin: 0, // No margins - content controls its own spacing
      filename: `${resumeData.personalInfo?.fullName || 'Resume'}_Resume.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 0.98 
      },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 794,
        scrollX: 0,
        scrollY: 0,
        timeout: 500
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.avoid-break'
      }
    };

    // Generate and download PDF
    try {
      await html2pdf().set(opt).from(resumeElement).save();
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar activeItem="history" />
        <div className="main-content">
          <div className="content-header">
            <h2>Question History</h2>
            <p className="loading-message">Loading your job history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <Sidebar activeItem="history" />
        <div className="main-content">
          <div className="content-header">
            <h2>Question History</h2>
            <p className="error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar activeItem="history" />
      <div className="main-content">
        <div className="content-header">
          <h2>History</h2>
          <p>View and revisit your past question sets and generated resumes.</p>
        </div>

        <div className="history-list">
          {/* Technical Interview Questions */}
          {jobs.length > 0 && (
            <div className="history-section">
              <h3>Technical Interview Questions</h3>
              {jobs.map((job) => (
                <div key={job.id} className="history-item sidebar-history-item-technical">
                  <div className="sidebar-history-item-type sidebar-history-item-type-technical">Technical</div>
                  <h4>{getJobTitle(job)}</h4>
                  <p>Generated on: {formatDate(job.created_at)}</p>
                  <p className={`status-${job.status.toLowerCase()}`}>
                    Status: {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                  </p>
                  {job.stem_field && <p>Field: {job.stem_field}</p>}
                  {job.difficulty && <p>Difficulty: {job.difficulty}</p>}
                  <button
                    onClick={() => navigate(`/questions?job_id=${job.id}`)}
                    className="view-btn"
                    disabled={job.status !== 'completed'}
                  >
                    {job.status === 'completed' ? 'View Questions' : 'Processing...'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* HR Screening Questions */}
          {hrQuestionSets.length > 0 && (
            <div className="history-section">
              <h3>HR Screening Interviews</h3>
              {hrQuestionSets.map((hrSet) => (
                <div key={hrSet.id} className="history-item sidebar-history-item-hr">
                  <div className="sidebar-history-item-type sidebar-history-item-type-hr">HR Screening</div>
                  <h4>{getHrQuestionSetTitle(hrSet)}</h4>
                  <p>Generated on: {formatDate(hrSet.created_at)}</p>
                  <p className={`status-${getHrQuestionSetStatus(hrSet).toLowerCase()}`}>
                    Status: {getHrQuestionSetStatus(hrSet) === 'completed_with_scoring' ? 'Completed with Analysis' : 
                             getHrQuestionSetStatus(hrSet) === 'completed' ? 'Completed' : 'In Progress'}
                  </p>
                  <p>Questions: {hrSet.questions_count}</p>
                  {hrSet.has_answers && <p>✓ Includes your answers</p>}
                  {hrSet.interview_performance_scoring && (
                    <div className="sidebar-history-item-score">
                      <span className="sidebar-history-item-score-label">Score:</span>
                      <span className="sidebar-history-item-score-value">{hrSet.interview_performance_scoring.overall_performance_score}/100</span>
                    </div>
                  )}
                  <button
                    onClick={() => navigate(`/hr-questions?session_id=${hrSet.id}`)}
                    className="view-btn hr-view-btn"
                  >
                    View HR Interview
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Resume History */}
          {resumes.length > 0 && (
            <div className="history-section">
              <h3>Generated Resumes</h3>
              {resumes.map((resume) => (
                <div key={resume.id} className="history-item sidebar-history-item-resume">
                  <div className="sidebar-history-item-type sidebar-history-item-type-resume">Resume</div>
                  <h4>{getResumeTitle(resume)}</h4>
                  <p>Generated on: {formatDate(resume.created_at)}</p>
                  <p className="status-completed">Status: Completed</p>
                  <button
                    onClick={() => handleDownloadResumePDF(resume)}
                    className="view-btn resume-view-btn"
                  >
                    Download Resume
                  </button>
                </div>
              ))}
            </div>
          )}

          {jobs.length === 0 && hrQuestionSets.length === 0 && resumes.length === 0 && (
            <div className="empty-state">
              <p>No past questions or resumes found.</p>
              <p className="suggestion">Create your first question set or resume to get started!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default History;
